import { Check, Copy, Mail, X } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { EmailInput } from "@/components/ui/EmailInput";
import { IconButton } from "@/components/ui/IconButton";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { siteOrigin } from "@/env";
import { addInvitees, removeInvitee } from "@/lib/api/meetings";
import type { Invitee, Meeting } from "@/lib/api/types";
import { useT } from "@/lib/i18n";

const STATUS_TONE = { pending: "neutral", accepted: "success", declined: "danger" } as const;
const STATUS_KEY = {
  pending: "invite.status.pending",
  accepted: "invite.status.accepted",
  declined: "invite.status.declined",
} as const;

export function InviteDialog({
  meeting,
  onClose,
  onChanged,
}: {
  meeting: Meeting;
  onClose: () => void;
  onChanged: (meeting: Meeting) => void;
}) {
  const toast = useToast();
  const [emails, setEmails] = useState<string[]>([]);
  const [invitees, setInvitees] = useState<Invitee[]>(meeting.invitees);
  const [busy, setBusy] = useState(false);
  const t = useT();

  const roomLink = `${siteOrigin()}/${meeting.roomSlug}`;

  const copy = (value: string, note: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        toast.show(note, "success");
      })
      .catch(() => {
        toast.show(value, "info");
      });
  };

  const add = async () => {
    if (emails.length === 0) return;
    setBusy(true);
    try {
      const added = await addInvitees(meeting.id, { emails });
      const next = [...invitees, ...added];
      setInvitees(next);
      onChanged({ ...meeting, invitees: next });
      setEmails([]);
      toast.show(t("invite.sent", { count: added.length }), "success");
    } catch (error) {
      toast.fail(error);
    } finally {
      setBusy(false);
    }
  };

  const drop = async (invitee: Invitee) => {
    try {
      await removeInvitee(meeting.id, invitee.id);
      const next = invitees.filter((entry) => entry.id !== invitee.id);
      setInvitees(next);
      onChanged({ ...meeting, invitees: next });
    } catch (error) {
      toast.fail(error);
    }
  };

  return (
    <Modal
      description={meeting.title}
      footer={
        <Button onClick={onClose} variant="secondary">
          {t("common.done")}
        </Button>
      }
      onClose={onClose}
      size="lg"
      title={t("invite.title")}
    >
      <div className="flex flex-col gap-5">
        <section>
          <h3 className="text-xs font-semibold tracking-wide text-ink-muted uppercase">
            {t("invite.anyoneWithLink")}
          </h3>
          <div className="mt-2 flex items-center gap-2 rounded-xl border border-line bg-surface-sunken px-3 py-2">
            <code className="min-w-0 flex-1 truncate font-mono text-xs text-ink">{roomLink}</code>
            <IconButton
              label={t("invite.copyLink")}
              onClick={() => {
                copy(roomLink, t("common.copied"));
              }}
            >
              <Copy className="h-4 w-4" />
            </IconButton>
          </div>
        </section>

        <section>
          <EmailInput
            exclude={invitees.map((invitee) => invitee.email)}
            hint={t("schedule.inviteHint")}
            label={t("schedule.inviteByEmail")}
            onChange={setEmails}
            placeholder={t("schedule.emailsPlaceholder")}
            value={emails}
          />
          <Button
            className="mt-2"
            disabled={emails.length === 0}
            loading={busy}
            onClick={() => {
              void add();
            }}
            size="sm"
          >
            <Mail className="h-4 w-4" /> {t("invite.send")}
          </Button>
        </section>

        {invitees.length > 0 ? (
          <section>
            <h3 className="text-xs font-semibold tracking-wide text-ink-muted uppercase">
              {t("invite.invitedCount", { count: invitees.length })}
            </h3>
            <ul className="mt-2 divide-y divide-line rounded-xl border border-line">
              {invitees.map((invitee) => (
                <li className="flex items-center gap-2 px-3 py-2" key={invitee.id}>
                  <span className="min-w-0 flex-1 truncate text-sm text-ink">
                    {invitee.displayName ?? invitee.email}
                  </span>
                  <Badge tone={STATUS_TONE[invitee.status]}>
                    {invitee.status === "accepted" ? <Check className="h-3 w-3" /> : null}
                    {t(STATUS_KEY[invitee.status])}
                  </Badge>
                  {invitee.token !== undefined ? (
                    <IconButton
                      label={t("invite.copyPersonal", { email: invitee.email })}
                      onClick={() => {
                        copy(
                          `${siteOrigin()}/invite/${invitee.token ?? ""}`,
                          t("invite.personalCopied"),
                        );
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </IconButton>
                  ) : null}
                  <IconButton
                    label={t("invite.remove", { email: invitee.email })}
                    onClick={() => {
                      void drop(invitee);
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </IconButton>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </Modal>
  );
}
